import { DocumentData } from "firebase/firestore";

type ClinicSession = {
	title?: string;
	label?: string;
	startDate?: string;
	endDate?: string;
	startTime?: string;
	endTime?: string;
};

const dateFormatter = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
});

const parseDateInput = (dateString?: string) => {
	if (!dateString) {
		return null;
	}
	const [year, month, day] = dateString.split("-").map(Number);
	if (!year || !month || !day) {
		return null;
	}
	return new Date(year, month - 1, day);
};

export const formatEventTime = (time?: string) => {
	if (!time) {
		return "";
	}
	const [hourValue, minute = "00"] = `${time}`.split(":");
	const hourNumber = Number(hourValue);
	if (!Number.isFinite(hourNumber)) {
		return time;
	}
	const period = hourNumber >= 12 ? "PM" : "AM";
	const hour = hourNumber % 12 || 12;
	const minuteLabel = minute === "00" ? "" : `:${minute}`;
	return `${hour}${minuteLabel} ${period}`;
};

export const formatDateRange = (startDate?: string, endDate?: string) => {
	const start = parseDateInput(startDate);
	const end = parseDateInput(endDate || startDate);
	if (!start) {
		return "";
	}
	if (!end || startDate === endDate || !endDate) {
		return dateFormatter.format(start);
	}
	return `${dateFormatter.format(start)} - ${dateFormatter.format(end)}`;
};

export const formatLeagueMatchTimes = (times?: string[]) => {
	if (!Array.isArray(times)) {
		return "";
	}
	return times.map((time) => formatEventTime(time)).filter(Boolean).join(", ");
};

export const getLeagueScheduleLabel = (event: DocumentData) => {
	const playDayLabel = event.leaguePlayDay
		? `Every ${event.leaguePlayDay}`
		: "";
	const dateLabel = formatDateRange(event.startDate, event.endDate);
	const matchTimes = formatLeagueMatchTimes(event.leagueMatchTimes);

	return [
		playDayLabel,
		dateLabel,
		matchTimes ? `Match times: ${matchTimes}` : "",
	]
		.filter(Boolean)
		.join(" · ");
};

export const getSessionTimeLabel = (session: ClinicSession) =>
	session.startTime && session.endTime
		? [formatEventTime(session.startTime), formatEventTime(session.endTime)]
				.filter(Boolean)
				.join(" - ")
		: "";

export const getSessionLabel = (session: ClinicSession) =>
	session?.label || getSessionTimeLabel(session);

export const getSessionDisplayLabel = (session: ClinicSession) =>
	[session?.title?.trim(), getSessionTimeLabel(session) || getSessionLabel(session)].filter(Boolean).join(" - ");

export const getRegisteredSessionDisplayLabel = (sessions: ClinicSession[] = [], label: string) => {
	const session = sessions.find(item => doesSessionMatchRegistration(item, label));
	return session ? getSessionDisplayLabel(session) : label;
};

export const getSessionDateTimeLabel = (session: ClinicSession) => {
	const dateLabel = formatDateRange(session.startDate, session.endDate);
	const timeLabel = getSessionTimeLabel(session);
	return [dateLabel, timeLabel].filter(Boolean).join(", ");
};

export const getSessionMatchLabels = (session: ClinicSession) =>
	Array.from(
		new Set(
			[session?.label, getSessionTimeLabel(session), getSessionDateTimeLabel(session)]
				.filter(Boolean),
		),
	);

const normalizeSessionMatchLabel = (label: string | null | undefined) =>
	`${label ?? ""}`.toLowerCase().replace(/\s+/g, "");

export const doesSessionMatchRegistration = (session: ClinicSession, label?: string | null) =>
	Boolean(label) && isSessionInList(session, [label!]);

export const isSessionInList = (
	session: ClinicSession,
	sessionList: string[] = [],
) => {
	const normalizedSessionList = new Set(
		(sessionList ?? []).map(normalizeSessionMatchLabel).filter(Boolean),
	);
	return getSessionMatchLabels(session).some((label) =>
		normalizedSessionList.has(normalizeSessionMatchLabel(label)),
	);
};

export const getEventScheduleLabel = (event: DocumentData) => {
	if (event.type === "League") {
		return getLeagueScheduleLabel(event);
	}

	if (event.multipleSessions === true) {
		const sessions = (event.sessions ?? []) as ClinicSession[];
		const dateLabels = (event.sessions ?? [])
			.map((session: ClinicSession) =>
				formatDateRange(session.startDate, session.endDate),
			)
			.filter(Boolean);
		const uniqueDateLabels = Array.from(new Set(dateLabels));
		const rangeStart = sessions
			.map((session) => session.startDate)
			.filter(Boolean)
			.sort()[0];
		const rangeEndOptions = sessions
			.map((session) => session.endDate || session.startDate)
			.filter(Boolean)
			.sort();
		const rangeEnd = rangeEndOptions[rangeEndOptions.length - 1];
		return uniqueDateLabels.length === 1
			? uniqueDateLabels[0]
			: formatDateRange(rangeStart, rangeEnd);
	}

	const structuredLabel = [
		formatDateRange(event.startDate, event.endDate),
		[formatEventTime(event.startTime), formatEventTime(event.endTime)]
			.filter(Boolean)
			.join(" - "),
	]
		.filter(Boolean)
		.join(", ");

	return structuredLabel;
};
